// Shared in-app feedback UI — the app-wide replacement for the browser's native
// alert() / confirm() / prompt() (which show a confusing "<site> says" header and a
// plain-OS look). Used by AI Insights and File Upload so every page speaks the same way:
//   • ToastView     — success / error / warning / info notification (auto-dismiss)
//   • ConfirmDialog — yes/no confirmation with a clear title, body and action buttons
//   • PromptDialog  — a single-line text input confirmation (replaces window.prompt)
//
// Presentational components take open/props + onConfirm/onCancel. For ergonomic use in
// async handlers, the promise-based hooks (useConfirm / usePrompt) let you write
//   if (!(await confirm({...}))) return;        // like `if (!window.confirm(...)) return;`
//   const name = await prompt({...});           // like `const name = window.prompt(...)`
// and useToast() gives { toast, notify, dismiss } for notifications.
import React, { useState, useRef, useCallback, useEffect } from "react";
import { CheckCircle2, AlertTriangle, Info, X as XIcon } from "lucide-react";

// ─── Toast ────────────────────────────────────────────────────────────────────────────────
const _TOAST_STYLES = {
  success: { bg: "#ecfdf5", border: "#a7f3d0", fg: "#047857", Icon: CheckCircle2 },
  error: { bg: "#fef2f2", border: "#fecaca", fg: "#b91c1c", Icon: AlertTriangle },
  warning: { bg: "#fffbeb", border: "#fde68a", fg: "#b45309", Icon: AlertTriangle },
  info: { bg: "#eff6ff", border: "#bfdbfe", fg: "#1d4ed8", Icon: Info },
};

export function ToastView({ toast, onClose }) {
  if (!toast) return null;
  const s = _TOAST_STYLES[toast.type] || _TOAST_STYLES.info;
  const Icon = s.Icon;
  return (
    // Sits BELOW the app's top bar (clear of the bell/avatar) and centered, with a solid
    // white card + colored accent bar so it's unmistakably visible over the dark UI.
    <div style={{ position: "fixed", top: 96, left: "50%", transform: "translateX(-50%)", zIndex: 10000, width: "min(460px, calc(100vw - 32px))" }}>
      <style>{"@keyframes toastIn{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:none}}"}</style>
      <div style={{
        display: "flex", gap: 12, alignItems: "flex-start", background: "#ffffff",
        border: `1px solid ${s.border}`, borderLeft: `5px solid ${s.fg}`, borderRadius: 12,
        padding: "14px 16px", boxShadow: "0 16px 40px rgba(2,6,23,.28)",
        animation: "toastIn .22s ease",
      }}>
        <span style={{ flexShrink: 0, marginTop: 1, width: 24, height: 24, borderRadius: 999, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={16} color={s.fg} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: s.fg }}>{toast.title}</div>
          {toast.message && <div style={{ fontSize: 13, color: "#1e293b", marginTop: 3, lineHeight: 1.45, whiteSpace: "pre-line" }}>{toast.message}</div>}
        </div>
        <button onClick={onClose} aria-label="Dismiss" style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 2, flexShrink: 0 }}>
          <XIcon size={16} />
        </button>
      </div>
    </div>
  );
}

// ─── Confirm ──────────────────────────────────────────────────────────────────────────────
const _CONFIRM_TONES = {
  danger: { accent: "#dc2626", btnBg: "#dc2626", btnHover: "#b91c1c", Icon: AlertTriangle, iconBg: "#fef2f2" },
  primary: { accent: "#079AFB", btnBg: "#079AFB", btnHover: "#0284c7", Icon: Info, iconBg: "#eff6ff" },
  warning: { accent: "#d97706", btnBg: "#d97706", btnHover: "#b45309", Icon: AlertTriangle, iconBg: "#fffbeb" },
};

export function ConfirmDialog({ open, title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", tone = "danger", onConfirm, onCancel }) {
  if (!open) return null;
  const t = _CONFIRM_TONES[tone] || _CONFIRM_TONES.danger;
  const Icon = t.Icon;
  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, zIndex: 10001, background: "rgba(2,6,23,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <style>{"@keyframes cdIn{from{opacity:0;transform:translateY(-10px) scale(.98)}to{opacity:1;transform:none}}"}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: "min(460px, calc(100vw - 32px))", background: "#ffffff", borderRadius: 14,
          borderTop: `5px solid ${t.accent}`, boxShadow: "0 24px 60px rgba(2,6,23,.4)",
          padding: "22px 22px 18px", animation: "cdIn .18s ease",
        }}
      >
        <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
          <span style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 999, background: t.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={20} color={t.accent} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: "#0f172a" }}>{title}</div>
            {message && <div style={{ fontSize: 13.5, color: "#475569", marginTop: 6, lineHeight: 1.5, whiteSpace: "pre-line" }}>{message}</div>}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <button
            onClick={onCancel}
            style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#ffffff", color: "#334155", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            onMouseEnter={(e) => { e.currentTarget.style.background = t.btnHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.btnBg; }}
            style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: t.btnBg, color: "#ffffff", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Prompt (single-line text input) ────────────────────────────────────────────────────────
export function PromptDialog({ open, title, message, placeholder = "", defaultValue = "", confirmLabel = "Save", cancelLabel = "Cancel", tone = "primary", onConfirm, onCancel }) {
  const t = _CONFIRM_TONES[tone] || _CONFIRM_TONES.primary;
  const Icon = t.Icon;
  const [val, setVal] = useState(defaultValue || "");
  useEffect(() => { if (open) setVal(defaultValue || ""); }, [open, defaultValue]);
  if (!open) return null;
  const submit = () => onConfirm && onConfirm(val);
  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, zIndex: 10001, background: "rgba(2,6,23,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <style>{"@keyframes cdIn{from{opacity:0;transform:translateY(-10px) scale(.98)}to{opacity:1;transform:none}}"}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: "min(460px, calc(100vw - 32px))", background: "#ffffff", borderRadius: 14,
          borderTop: `5px solid ${t.accent}`, boxShadow: "0 24px 60px rgba(2,6,23,.4)",
          padding: "22px 22px 18px", animation: "cdIn .18s ease",
        }}
      >
        <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
          <span style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 999, background: t.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={20} color={t.accent} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: "#0f172a" }}>{title}</div>
            {message && <div style={{ fontSize: 13.5, color: "#475569", marginTop: 6, lineHeight: 1.5, whiteSpace: "pre-line" }}>{message}</div>}
            <input
              autoFocus
              value={val}
              placeholder={placeholder}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); else if (e.key === "Escape") onCancel && onCancel(); }}
              style={{ marginTop: 12, width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 9, border: "1px solid #cbd5e1", fontSize: 14, color: "#0f172a", outline: "none" }}
            />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <button
            onClick={onCancel}
            style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#ffffff", color: "#334155", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={submit}
            onMouseEnter={(e) => { e.currentTarget.style.background = t.btnHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.btnBg; }}
            style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: t.btnBg, color: "#ffffff", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────────────────
// Styled toast with auto-dismiss. notify(type, title, message?) shows it; dismiss() hides it.
export function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const notify = useCallback((type, title, message = "") => {
    setToast({ type, title, message, key: Date.now() });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 4800);
  }, []);
  const dismiss = useCallback(() => { if (timer.current) clearTimeout(timer.current); setToast(null); }, []);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return { toast, notify, dismiss };
}

// Promise-based confirm: `if (!(await confirm({title, message, tone, confirmLabel})) ) return;`.
// Spread confirmDialogProps onto <ConfirmDialog />.
export function useConfirm() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setState(opts || {});
  }), []);
  const settle = useCallback((result) => {
    setState(null);
    const r = resolver.current; resolver.current = null;
    if (r) r(result);
  }, []);
  const confirmDialogProps = { open: !!state, ...(state || {}), onConfirm: () => settle(true), onCancel: () => settle(false) };
  return { confirm, confirmDialogProps };
}

// Promise-based prompt: `const value = await prompt({title, message, placeholder, defaultValue})`.
// Resolves to the entered string, or null if cancelled. Spread promptDialogProps onto <PromptDialog />.
export function usePrompt() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const prompt = useCallback((opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setState(opts || {});
  }), []);
  const settle = useCallback((value) => {
    setState(null);
    const r = resolver.current; resolver.current = null;
    if (r) r(value);
  }, []);
  const promptDialogProps = { open: !!state, ...(state || {}), onConfirm: (v) => settle(v), onCancel: () => settle(null) };
  return { prompt, promptDialogProps };
}
